/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

/**
 * Controller for the telemetry performance page.
 */
console.log('[Telemetry Performance] Controller file loaded - registering controller...');
angular.module('settings').controller('telemetryPerformanceController', 
    ['$scope', '$injector', '$routeParams', '$location', '$interval',
    function telemetryPerformanceController($scope, $injector, $routeParams, $location, $interval) {

    console.log('[Telemetry Performance] Controller function executing!');
    
    // Required services
    var authenticationService = $injector.get('authenticationService');
    var requestService = $injector.get('requestService');
    
    // Get telemetry service if available (for fallback to frontend data)
    var qinayaTelemetryService = null;
    try {
        qinayaTelemetryService = $injector.get('qinayaTelemetryService');
    } catch (e) {
        // Service may not be available in settings context
    }

    /**
     * The session identifier for which telemetry is being displayed.
     * This MUST be a sessionId (UUID) - we no longer accept connectionId.
     */
    $scope.sessionId = $routeParams.sessionId || $routeParams.connectionId; // Support both param names for transition
    $scope.connectionId = null;  // Will be set from telemetry data
    
    // Validate that we have a sessionId (UUID format)
    if (!$scope.sessionId || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test($scope.sessionId)) {
        console.error('[Telemetry Performance] ERROR: Invalid or missing sessionId. Expected UUID format, got:', $scope.sessionId);
        $scope.loadingComplete = true;
        $scope.error = 'Invalid session identifier. Expected a session ID (UUID format).';
    }
    
    console.log('[Telemetry Performance] Controller initialized');
    console.log('[Telemetry Performance] Route params:', $routeParams);
    console.log('[Telemetry Performance] SessionId from route:', $scope.sessionId);

    /**
     * The telemetry data for the connection, or null if not yet loaded.
     */
    $scope.telemetryData = null;

    /**
     * Whether the initial data fetch has completed (regardless of whether data was found).
     */
    $scope.loadingComplete = false;

    /**
     * Whether data has finished loading.
     */
    $scope.isLoaded = function isLoaded() {
        return $scope.loadingComplete;
    };

    /**
     * Returns the CSS class for latency color coding.
     */
    $scope.getLatencyClass = function getLatencyClass(latency) {
        if (latency == null) return '';
        if (latency < 70) return 'latency-green';
        if (latency < 114) return 'latency-yellow';
        return 'latency-red';
    };

    /**
     * Returns the CSS class for connection rating.
     */
    $scope.getRatingClass = function getRatingClass(rating) {
        if (!rating) return '';
        rating = rating.toLowerCase();
        if (rating === 'excellent' || rating === 'good') return 'rating-good';
        if (rating === 'fair') return 'rating-fair';
        return 'rating-poor';
    };

    /**
     * Formats bytes into a human-readable string (KB, MB, GB).
     */
    $scope.formatBytes = function formatBytes(bytes) {
        if (bytes == null || bytes === 0)
            return '0 B';
        
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    /**
     * Formats a date/time string for display.
     */
    $scope.formatDateTime = function formatDateTime(dateTimeString) {
        if (!dateTimeString) return '—';
        
        try {
            var date = new Date(dateTimeString);
            if (isNaN(date.getTime())) return dateTimeString; // Return as-is if invalid
            
            // Format as: "Jan 9, 2026 5:30:45 PM"
            return date.toLocaleString();
        } catch (e) {
            return dateTimeString; // Return as-is if parsing fails
        }
    };

    /**
     * Checks if the session is still active (no end time).
     */
    $scope.isSessionActive = function isSessionActive() {
        if (!$scope.telemetryData) {
            console.log('[Telemetry Performance] isSessionActive: No telemetryData');
            return false;
        }
        var isActive = !$scope.telemetryData.sessionEndTime;
        console.log('[Telemetry Performance] isSessionActive: sessionEndTime=' + $scope.telemetryData.sessionEndTime + ', isActive=' + isActive);
        return isActive;
    };

    /**
     * Converts frontend telemetry metrics to API format.
     */
    var convertFrontendToAPIData = function convertFrontendToAPIData(sessionId) {
        if (!qinayaTelemetryService || !sessionId)
            return null;

        // Query by sessionId (the service will resolve it)
        var rating = qinayaTelemetryService.getConnectionRating(sessionId);
        
        return {
            sessionId: sessionId,  // Use the provided sessionId
            connectionId: null,  // Will be populated from metrics if available
            username: '',
            sessionStartTime: qinayaTelemetryService.getSessionStartTime(sessionId),
            sessionEndTime: qinayaTelemetryService.getSessionEndTime(sessionId),
            avgLatencyMs: qinayaTelemetryService.getAverageLatency(sessionId),
            minLatencyMs: qinayaTelemetryService.getMinLatency(sessionId),
            maxLatencyMs: qinayaTelemetryService.getMaxLatency(sessionId),
            currentLatencyMs: qinayaTelemetryService.getCurrentLatency(sessionId),
            avgJitterMs: qinayaTelemetryService.getJitter(sessionId),
            latencySpikesCount: qinayaTelemetryService.getLatencySpikesCount(sessionId),
            packetLossPercent: qinayaTelemetryService.getPacketLossPercent(sessionId),
            connectionLossesCount: qinayaTelemetryService.getConnectionLossCount(sessionId),
            disconnectionCount: qinayaTelemetryService.getDisconnectionCount(sessionId),
            connectionRating: rating ? rating.rating : null,
            connectionRatingScore: rating ? rating.score : null,
            totalBytesSent: qinayaTelemetryService.getBytesSent(sessionId),
            totalBytesReceived: qinayaTelemetryService.getBytesReceived(sessionId),
            avgUploadSpeedKbps: qinayaTelemetryService.getUploadSpeedKbps(sessionId),
            avgDownloadSpeedKbps: qinayaTelemetryService.getDownloadSpeedKbps(sessionId)
        };
    };

    /**
     * Fetches telemetry data from the API, with fallback to frontend service.
     */
    var fetchTelemetryData = function fetchTelemetryData() {
        if (!$scope.sessionId) {
            console.warn('[Telemetry Performance] No sessionId provided');
            $scope.loadingComplete = true;
            return;
        }

        // ALWAYS query by sessionId - never use connectionId
        console.log('[Telemetry Performance] Fetching data for sessionId:', $scope.sessionId);
        console.log('[Telemetry Performance] API URL: api/telemetry/sessions/' + encodeURIComponent($scope.sessionId));

        authenticationService.request({
            method: 'GET',
            url: 'api/telemetry/sessions/' + encodeURIComponent($scope.sessionId)
        }).then(function(telemetry) {
            console.log('[Telemetry Performance] API response:', telemetry);
            console.log('[Telemetry Performance] API response sessionEndTime:', telemetry ? telemetry.sessionEndTime : 'null');
            
            // If backend has data, use it; otherwise try frontend service by sessionId
            if (telemetry && (telemetry.avgLatencyMs != null || telemetry.currentLatencyMs != null)) {
                console.log('[Telemetry Performance] Using backend data');
                $scope.telemetryData = telemetry;
                $scope.connectionId = telemetry.connectionId; // Set connectionId from telemetry data for display
                console.log('[Telemetry Performance] Set telemetryData.sessionEndTime to:', $scope.telemetryData.sessionEndTime);
            } else {
                console.log('[Telemetry Performance] Backend data insufficient, trying frontend service');
                // Fallback to frontend telemetry service - query by sessionId
                var frontendData = convertFrontendToAPIData($scope.sessionId);
                console.log('[Telemetry Performance] Frontend data:', frontendData);
                console.log('[Telemetry Performance] Frontend data sessionEndTime:', frontendData ? frontendData.sessionEndTime : 'null');
                $scope.telemetryData = frontendData;
                if (frontendData) {
                    $scope.connectionId = frontendData.connectionId;
                }
            }
            $scope.loadingComplete = true;
            
            // If session has ended, stop polling
            if ($scope.telemetryData && $scope.telemetryData.sessionEndTime) {
                console.log('[Telemetry Performance] Session has ended (sessionEndTime=' + $scope.telemetryData.sessionEndTime + '), stopping updates');
                if (updateInterval) {
                    $interval.cancel(updateInterval);
                    updateInterval = null;
                }
            } else {
                console.log('[Telemetry Performance] Session is still active (sessionEndTime is null/undefined)');
            }
        }).catch(function(error) {
            console.warn('[Telemetry Performance] API request failed:', error);
            // On error, try frontend service as fallback - query by sessionId
            var frontendData = convertFrontendToAPIData($scope.sessionId);
            console.log('[Telemetry Performance] Using frontend fallback data:', frontendData);
            $scope.telemetryData = frontendData;
            if (frontendData) {
                $scope.connectionId = frontendData.connectionId;
            }
            $scope.loadingComplete = true;
            // Force digest cycle to update view
            if (!$scope.$$phase && !$scope.$root.$$phase) {
                $scope.$apply();
            }
            
            // If session has ended, stop polling
            if ($scope.telemetryData && $scope.telemetryData.sessionEndTime && updateInterval) {
                console.log('[Telemetry Performance] Session has ended, stopping updates');
                $interval.cancel(updateInterval);
                updateInterval = null;
            }
        });
    };

    /**
     * Navigates back to the sessions page.
     */
    $scope.goBack = function goBack() {
        $location.path('/settings/sessions');
    };

    /**
     * Gets all sessions for the current connection (for statistics).
     * This can be used to display historical data across multiple sessions.
     *
     * @returns {Promise}
     *     A promise that resolves with an array of all sessions for this connection.
     */
    $scope.getAllSessionsForConnection = function getAllSessionsForConnection() {
        if (!$scope.connectionId || $scope.isSessionId) {
            // If we're viewing by sessionId, we need the connectionId from the loaded data
            if ($scope.telemetryData && $scope.telemetryData.connectionId) {
                var connId = $scope.telemetryData.connectionId;
                return authenticationService.request({
                    method: 'GET',
                    url: 'api/telemetry/sessions/connection/' + encodeURIComponent(connId)
                });
            }
            return Promise.resolve([]);
        }
        
        return authenticationService.request({
            method: 'GET',
            url: 'api/telemetry/sessions/connection/' + encodeURIComponent($scope.connectionId)
        });
    };

    /**
     * Gets all sessions across all connections (for global statistics).
     *
     * @returns {Promise}
     *     A promise that resolves with an array of all sessions.
     */
    $scope.getAllSessions = function getAllSessions() {
        return authenticationService.request({
            method: 'GET',
            url: 'api/telemetry/sessions/all'
        });
    };

    // Declare update interval variable (will be set below)
    var updateInterval = null;

    // Log when controller is ready
    console.log('[Telemetry Performance] Controller ready, connectionId:', $scope.connectionId);
    
    // Fetch data on load
    if ($scope.connectionId) {
        console.log('[Telemetry Performance] Starting initial data fetch...');
        fetchTelemetryData();
    } else {
        console.error('[Telemetry Performance] ERROR: No connectionId available!');
        console.error('[Telemetry Performance] Route params:', $routeParams);
        $scope.loadingComplete = true;
    }

    // Update data every 1 second for real-time updates (only if session is still active)
    updateInterval = $interval(function() {
        if ($scope.sessionId) {
            // Only poll if session is still active (no end time)
            if (!$scope.telemetryData || !$scope.telemetryData.sessionEndTime) {
                fetchTelemetryData();
            }
        }
    }, 1000);

    // Clean up interval on scope destroy
    $scope.$on('$destroy', function() {
        if (updateInterval) {
            $interval.cancel(updateInterval);
        }
    });

}]);

